import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_CMS_API_URL || 'https://marveland-cms.jimsbond007.workers.dev';
const VISITOR_ID_KEY = 'marveland_visitor_id';
const SESSION_ID_KEY = 'marveland_session_id';

interface AnalyticsContextValue {
  trackEvent: (meta?: Record<string, any>) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

function generateVisitorId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function getVisitorId(): string {
  if (typeof window === 'undefined') {
    return `ssr-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  // Cross-session persistent visitor ID: same device/browser = same ID
  // until the user clears localStorage.
  const persistentId = localStorage.getItem(VISITOR_ID_KEY);
  if (persistentId) {
    sessionStorage.setItem(SESSION_ID_KEY, persistentId);
    return persistentId;
  }

  const existing = sessionStorage.getItem(SESSION_ID_KEY);
  if (existing) {
    localStorage.setItem(VISITOR_ID_KEY, existing);
    return existing;
  }

  const id = generateVisitorId();
  sessionStorage.setItem(SESSION_ID_KEY, id);
  localStorage.setItem(VISITOR_ID_KEY, id);
  return id;
}

function getUtmParams(search: string) {
  const params = new URLSearchParams(search);
  return {
    utmSource: params.get('utm_source') || undefined,
    utmMedium: params.get('utm_medium') || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
    utmContent: params.get('utm_content') || undefined,
  };
}

async function sendEvent(payload: {
  type: 'pageview' | 'conversion';
  path: string;
  referrer?: string;
  sessionId: string;
  timestamp: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  meta?: Record<string, any>;
}) {
  if (!API_BASE) return;
  try {
    await fetch(`${API_BASE}/api/analytics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    // silent fail
  }
}

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const visitorId = useRef(getVisitorId());
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;

    const utm = getUtmParams(location.search);
    sendEvent({
      type: 'pageview',
      path: location.pathname + location.search,
      referrer: document.referrer || undefined,
      sessionId: visitorId.current,
      timestamp: new Date().toISOString(),
      ...utm,
    });
  }, [location]);

  const trackEvent = useCallback((meta?: Record<string, any>) => {
    const utm = getUtmParams(window.location.search);
    sendEvent({
      type: 'conversion',
      path: window.location.pathname + window.location.search,
      referrer: document.referrer || undefined,
      sessionId: visitorId.current,
      timestamp: new Date().toISOString(),
      ...utm,
      meta,
    });
  }, []);

  return <AnalyticsContext.Provider value={{ trackEvent }}>{children}</AnalyticsContext.Provider>;
};

export const useTrackEvent = () => {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) throw new Error('useTrackEvent must be used within AnalyticsProvider');
  return ctx.trackEvent;
};
